import React, { useState } from 'react';
import { useApp } from '../../../context/AppContext';
import { Card, CardHeader, CardTitle, Btn, InfoBanner, FormGrid, FormGroup, Input, CustomSelect, Textarea, SectionHead, Modal } from '../../common/UI';

const TXN_TYPES = [
  { id: 'Incoming', label: 'Stock List (Incoming)', sub: 'New cargo entering the warehouse', icon: '📦', accent: 'var(--accent)', bg: 'var(--accent-light)' },
  { id: 'Offland', label: 'Offland (Return From Vessel)', sub: 'Items being returned from vessel', icon: '🔄', accent: 'var(--orange)', bg: 'var(--orange-bg)' },
];

const PKG_OPTIONS = ['PKG', 'CTN', 'PLT', 'DRM', 'BOX', 'BAG'];
const UNIT_OPTIONS = ['KGS', 'LBS', 'MT'];

const STEPS = [
  { num: 1, title: 'Company' },
  { num: 2, title: 'Process Type' },
  { num: 3, title: 'Vessel & Shipping' },
  { num: 4, title: 'Cargo Details' },
  { num: 5, title: 'Photo Evidence' },
];

const EMPTY = {
  type: '', customer: '', vessel: '', supplier: '', po: '',
  awb: '', port: '', frtType: '', pkgType: 'PKG', unit: 'KGS',
  qty: '', weight: '', description: '', location: '', origin: '', remarks: '', photos: [], docs: [],
  dimList: [{ length: '', width: '', height: '' }],
  date: new Date().toISOString().split('T')[0]
};

export default function NewEntry({ onNav, draftData }) {
  const { customers, vessels, suppliers, locations, freightTypes, countries, addStock, showToast, saveDraft: ctxSaveDraft, deleteDraft } = useApp();

  // Map DB draft fields to form fields if coming from a stock entry
  const initForm = () => {
    if (!draftData) return { ...EMPTY };
    // If it's a DB draft (has customer_id), map DB fields -> form fields
    if (draftData.customer_id !== undefined) {
      const storedDims = draftData.dimensions || draftData.dimension || '';
      const dimArray = storedDims ? storedDims.split(',').map(d => {
        const [l, w, h] = d.trim().split('X');
        return { length: l || '', width: w || '', height: h || '' };
      }) : [{ length: '', width: '', height: '' }];
      
      return {
        ...EMPTY,
        customer: String(draftData.customer_id || ''),
        type: draftData.process_type === 'offland' ? 'Offland' : draftData.process_type ? 'Incoming' : '',
        vessel: String(draftData.vessel_id || ''),
        supplier: String(draftData.supplier_id || ''),
        po: draftData.po_numbers || draftData.po || '',
        awb: draftData.awb_bl_number || draftData.awb || '',
        port: draftData.port || '',
        frtType: String(draftData.freight_type_id || ''),
        pkgType: draftData.pkg_type || draftData.pkgType || 'PKG',
        unit: draftData.packaging_unit || draftData.unit || 'KGS',
        qty: String(draftData.quantity || draftData.qty || ''),
        weight: String(draftData.weight || ''),
        dimList: dimArray,
        description: draftData.description || '',
        location: String(draftData.warehouse_location_id || ''),
        origin: String(draftData.origin_country_id || ''),
        remarks: draftData.remarks || '',
        photos: [],
        date: draftData.entry_date ? draftData.entry_date.split('T')[0] : new Date().toISOString().split('T')[0]
      };
    }
    // localStorage-style draft (has form field names directly)
    return { ...EMPTY, ...draftData };
  };

  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(draftData?.currentStep || draftData?.current_step || 1);
  const [draftId, setDraftId] = useState(draftData?.id || null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refNum, setRefNum] = useState('');

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); if (errors[k]) setErrors(p => ({ ...p, [k]: undefined })); };

  /* ── Per-step validation ── */
  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.customer) e.customer = 'Select a company';
    }
    if (s === 2) {
      if (!form.type) e.type = 'Select a process type';
    }
    if (s === 3) {
      if (!form.vessel) e.vessel = 'Select vessel';
      if (!form.supplier) e.supplier = 'Select supplier';
      if (!form.frtType) e.frtType = 'Select freight type';
      if (!form.awb.trim()) e.awb = 'Enter AWB / BL number';
      if (!form.po.trim()) e.po = 'Enter PO number(s)';
      else if (!form.po.trim().startsWith('PO:')) e.po = 'Format must start with PO:';
    }
    if (s === 4) {
      if (!form.qty || +form.qty < 1) e.qty = 'Quantity must be ≥ 1';
      if (!form.weight || +form.weight <= 0) e.weight = 'Enter weight > 0';
      if (form.dimList.some(d => !d.length || !d.width || !d.height)) e.dims = 'All dimension fields are mandatory';
      if (!form.description.trim()) e.description = 'Enter cargo description';
      if (!form.location) e.location = 'Select location';
    }
    if (s === 5) {
      if (form.photos.length === 0 && form.docs.length === 0) e.photos = 'Upload at least 1 photo or document (mandatory)';
    }
    return e;
  };

  const goNext = () => {
    const e = validateStep(step);
    setErrors(e);
    if (Object.keys(e).length > 0) { showToast('Please fix required fields', 'error'); return; }
    if (step < 5) setStep(step + 1);
  };

  const goBack = () => { if (step > 1) setStep(step - 1); };

  /* Real photo upload and submit */
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (form.photos.length + files.length > 10) {
      showToast('Maximum 10 photos allowed', 'error'); return;
    }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(p => ({
          ...p, photos: [...p.photos, {
            id: Date.now() + Math.random(),
            file: file, // Store the raw file object
            name: file.name,
            preview: ev.target.result,
            size: (file.size / 1024).toFixed(1) + ' KB'
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleRemovePhoto = (id) => setForm(p => ({ ...p, photos: p.photos.filter(x => x.id !== id) }));

  const handleDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm(p => ({
          ...p, docs: [...p.docs, {
            id: Date.now() + Math.random(),
            file: file,
            name: file.name,
            size: (file.size / 1024).toFixed(1) + ' KB'
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const removeDoc = (id) => setForm(p => ({ ...p, docs: p.docs.filter(x => x.id !== id) }));

  const handleSubmit = async () => {
    const e = validateStep(5);
    setErrors(e);
    if (Object.keys(e).length > 0) { showToast('Please fix required fields', 'error'); return; }

    // Prepare FormData
    const fd = new FormData();
    fd.append('process_type', form.type.toLowerCase() === 'incoming' ? 'incoming' : 'offland');
    fd.append('customer_id', form.customer);
    fd.append('vessel_id', form.vessel);
    fd.append('supplier_id', form.supplier);
    fd.append('awb_bl_number', form.awb);
    fd.append('po_numbers', form.po);
    fd.append('quantity', form.qty);
    fd.append('weight', form.weight);
    fd.append('dimensions', form.dimList.map(d => `${d.length}X${d.width}X${d.height}`).join(', '));
    fd.append('freight_type_id', form.frtType);
    if (form.origin) fd.append('origin_country_id', form.origin);
    fd.append('warehouse_location_id', form.location);
    if (form.port) fd.append('port', form.port);
    fd.append('description', form.description);
    if (form.remarks) fd.append('remarks', form.remarks);
    fd.append('pkg_type', form.pkgType);
    fd.append('packaging_unit', form.unit);
    fd.append('entry_date', form.date);

    // Append photos
    form.photos.forEach(p => {
      fd.append('photos', p.file);
    });
    // Append documents
    form.docs.forEach(d => {
      fd.append('photos', d.file); // Backend handles all in req.files
    });

    setIsSubmitting(true);
    try {
      const newId = await addStock(fd);
      setRefNum(`ST-${newId}`);
      setShowSuccess(true);
    } catch (err) {
      // Error handled by addStock toast
    } finally {
      setIsSubmitting(false);
    }
  };
  const removePhoto = (id) => set('photos', form.photos.filter(p => p.id !== id));

  const handleDraftSave = () => {
    // Save form data with current step
    const customers_name = customers.find(c => String(c.id) === String(form.customer))?.company || '';
    ctxSaveDraft({ ...form, current_step: step, draftId, customerName: customers_name });
    showToast('✏ Draft saved! Find it in Stock List → Drafts tab.', 'success');
    onNav('stock-list');
  };

  const reset = () => {
    setForm({ ...EMPTY });
    setErrors({});
    setStep(1);
    setDraftId(null);
    setShowSuccess(false);
  };

  return (
    <div className="fade-up" style={{ padding: 24 }}>
      <SectionHead title="Add Stock Entry" subtitle="Fill each step and click Next to proceed">
        <Btn variant="outline" onClick={() => onNav('stock-list')}>← Back to Stock List</Btn>
      </SectionHead>

      {/* ── Step Progress Bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 20, padding: '0 4px' }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={s.num}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: s.num < step ? 'pointer' : 'default' }}
              onClick={() => { if (s.num < step) setStep(s.num); }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, transition: 'all .2s',
                background: step === s.num ? 'var(--accent)' : s.num < step ? 'var(--success)' : 'var(--surface2)',
                color: step === s.num || s.num < step ? '#fff' : 'var(--muted)',
                border: step === s.num ? '2px solid var(--accent)' : s.num < step ? '2px solid var(--success)' : '2px solid var(--border)',
              }}>
                {s.num < step ? '✓' : s.num}
              </div>
              <span style={{
                fontSize: 12.5, fontWeight: step === s.num ? 700 : 500,
                color: step === s.num ? 'var(--text)' : s.num < step ? 'var(--success)' : 'var(--muted)',
                whiteSpace: 'nowrap'
              }}>{s.title}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1 }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── STEP 1: Select Customer ── */}
      {step === 1 && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader><CardTitle>Step 1 Select Company <span style={{ color: 'var(--danger)' }}>*</span></CardTitle></CardHeader>
          <div style={{ padding: 18 }}>
            <FormGroup label="Company Name" required error={errors.customer}>
              <CustomSelect value={form.customer} onChange={e => { set('customer', e.target.value); set('vessel', ''); }} style={{ fontSize: 15, padding: '12px 14px' }}>
                <option value="">Select Company...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company}</option>)}
              </CustomSelect>
            </FormGroup>
          </div>
        </Card>
      )}

      {/* ── STEP 2: Process Type ── */}
      {step === 2 && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader><CardTitle>Step 2 Process Type <span style={{ color: 'var(--danger)' }}>*</span></CardTitle></CardHeader>
          <div style={{ padding: 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
              {TXN_TYPES.map(t => (
                <div key={t.id} onClick={() => set('type', t.id)}
                  style={{
                    border: `2px solid ${form.type === t.id ? t.accent : 'var(--border)'}`,
                    borderRadius: 12, padding: 18, cursor: 'pointer', transition: 'all .15s',
                    background: form.type === t.id ? t.bg : 'var(--white)', position: 'relative'
                  }}
                  onMouseEnter={e => { if (form.type !== t.id) e.currentTarget.style.borderColor = t.accent; }}
                  onMouseLeave={e => { if (form.type !== t.id) e.currentTarget.style.borderColor = 'var(--border)'; }}>
                  {form.type === t.id && (
                    <div style={{
                      position: 'absolute', top: 10, right: 10, width: 20, height: 20, borderRadius: '50%',
                      background: t.accent, color: '#fff', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 11, fontWeight: 700
                    }}>✓</div>
                  )}
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{t.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', marginBottom: 4 }}>{t.label}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>{t.sub}</div>
                </div>
              ))}
            </div>
            {errors.type && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 8 }}>⚠ {errors.type}</div>}
          </div>
        </Card>
      )}

      {/* ── STEP 3: Vessel, Supplier & Shipping ── */}
      {step === 3 && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader><CardTitle>Step 3 Vessel, Supplier & Shipping Info</CardTitle></CardHeader>
          <div style={{ padding: 18 }}>
            <InfoBanner style={{ marginBottom: 14 }}>ℹ PO Numbers cannot be edited after saving. Please double-check before proceeding.</InfoBanner>
            <FormGrid>
              <FormGroup label="Vessel Name" required error={errors.vessel}>
                <CustomSelect value={form.vessel} onChange={e => set('vessel', e.target.value)}>
                  <option value="">Select vessel...</option>
                  {vessels
                    .filter(v => !form.customer || v.customerIds?.some(id => id === +form.customer))
                    .map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </CustomSelect>
                {form.customer && vessels.filter(v => v.customerIds?.some(id => id === +form.customer)).length === 0 && (
                  <span style={{ fontSize: 11, color: 'var(--warning)', marginTop: 2 }}>No vessels found for this company</span>
                )}
              </FormGroup>
              <FormGroup label="Supplier" required error={errors.supplier}>
                <CustomSelect value={form.supplier} onChange={e => set('supplier', e.target.value)}>
                  <option value="">Select supplier...</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </CustomSelect>
              </FormGroup>
              <FormGroup label="FRT Type" required error={errors.frtType}>
                <CustomSelect value={form.frtType} onChange={e => set('frtType', e.target.value)}>
                  <option value="">Select freight type...</option>
                  {freightTypes.map(f => <option key={f.id} value={f.id}>{f.name} ({f.mode})</option>)}
                </CustomSelect>
              </FormGroup>
              <FormGroup label="AWB / BL / LR Number" required error={errors.awb} hint="Airway Bill, Bill of Lading, or Lorry Receipt">
                <Input value={form.awb} onChange={e => set('awb', e.target.value)}
                  placeholder="e.g. 618 51335045"
                  style={{ fontFamily: "'JetBrains Mono',monospace" }} />
              </FormGroup>
              <FormGroup label="Port" hint="Origin port code">
                <Input value={form.port} onChange={e => set('port', e.target.value)}
                  placeholder="e.g. SIN, OSA, PUS" />
              </FormGroup>
              <FormGroup label="PO Number(s)" required hint="Not editable after save" error={errors.po} full>
                <Input value={form.po} onChange={e => set('po', e.target.value)}
                  placeholder="e.g. PE0825027901"
                  style={{ fontFamily: "'JetBrains Mono',monospace" }} />
              </FormGroup>
            </FormGrid>
          </div>
        </Card>
      )}

      {/* ── STEP 4: Cargo Details ── */}
      {step === 4 && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader><CardTitle>Step 4 Cargo Details</CardTitle></CardHeader>
          <div style={{ padding: 18 }}>
            <FormGrid>
              <FormGroup label="Manual Entry Date" required error={errors.date} hint="Select the actual entry date">
                <Input type="date" value={form.date} onChange={e => set('date', e.target.value)} />
              </FormGroup>
              <FormGroup label="Quantity" required error={errors.qty}>
                <Input type="number" min="1" value={form.qty} onChange={e => {
                  const newQty = parseInt(e.target.value) || 0;
                  set('qty', e.target.value);
                  setForm(p => {
                    const newList = [...p.dimList];
                    if (newQty > newList.length && newQty <= 100) {
                      while(newList.length < newQty) newList.push({ length: '', width: '', height: '' });
                    } else if (newQty < newList.length && newQty > 0) {
                      newList.length = newQty;
                    }
                    return { ...p, dimList: newList };
                  });
                }} placeholder="0" />
              </FormGroup>
              <FormGroup label="Package Type">
                <CustomSelect value={form.pkgType} onChange={e => set('pkgType', e.target.value)}>
                  {PKG_OPTIONS.map(p => <option key={p}>{p}</option>)}
                </CustomSelect>
              </FormGroup>
              <FormGroup label="Weight" required error={errors.weight}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Input type="number" step="0.01" value={form.weight} onChange={e => set('weight', e.target.value)}
                    placeholder="0.00" style={{ flex: 1 }} />
                  <CustomSelect value={form.unit} onChange={e => set('unit', e.target.value)} style={{ width: 80 }}>
                    {UNIT_OPTIONS.map(u => <option key={u}>{u}</option>)}
                  </CustomSelect>
                </div>
              </FormGroup>
              <FormGroup label="Description" required error={errors.description}>
                <Input value={form.description} onChange={e => set('description', e.target.value)}
                  placeholder="e.g. SHIP SPARE, AUXILIARY ENGINE" />
              </FormGroup>
              
              {/* Dynamic Dimensions Block */}
              <div style={{ gridColumn: '1 / -1', background: 'var(--surface2)', padding: 16, borderRadius: 12, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>Dimensions for {form.dimList.length} Item(s)</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {form.dimList.map((dim, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                      <FormGroup label={`#${idx+1} Length (cm)`} required>
                        <Input type="number" value={dim.length} onChange={e => {
                          const nl = [...form.dimList]; nl[idx].length = e.target.value; set('dimList', nl);
                        }} placeholder="0" />
                      </FormGroup>
                      <FormGroup label={`#${idx+1} Width (cm)`} required>
                        <Input type="number" value={dim.width} onChange={e => {
                          const nl = [...form.dimList]; nl[idx].width = e.target.value; set('dimList', nl);
                        }} placeholder="0" />
                      </FormGroup>
                      <FormGroup label={`#${idx+1} Height (cm)`} required>
                        <Input type="number" value={dim.height} onChange={e => {
                          const nl = [...form.dimList]; nl[idx].height = e.target.value; set('dimList', nl);
                        }} placeholder="0" />
                      </FormGroup>
                    </div>
                  ))}
                  {errors.dims && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>⚠ {errors.dims}</div>}
                </div>
              </div>
              <FormGroup label="Warehouse Location" required error={errors.location}>
                <CustomSelect value={form.location} onChange={e => set('location', e.target.value)}>
                  <option value="">Select location...</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.code} {l.name}</option>)}
                </CustomSelect>
              </FormGroup>
              <FormGroup label="Origin Country">
                <CustomSelect value={form.origin} onChange={e => set('origin', e.target.value)}>
                  <option value="">Select country...</option>
                  {countries.map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
                </CustomSelect>
              </FormGroup>
              <FormGroup label="Remarks / Additional Notes" full>
                <Textarea value={form.remarks} onChange={e => set('remarks', e.target.value)}
                  placeholder="e.g. CARGO CLEARED AND KEPT IN CS" />
              </FormGroup>
            </FormGrid>
          </div>
        </Card>
      )}

      {/* ── STEP 5: Photo Upload from Gallery ── */}
      {step === 5 && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader>
            <CardTitle>Step 5 Photo Evidence <span style={{ color: 'var(--danger)' }}>*</span></CardTitle>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Min 1 required · {form.photos.length}/10 uploaded</span>
          </CardHeader>
          <div style={{ padding: 18 }}>
            <input type="file" id="photoInput" accept="image/*,.pdf" multiple
              style={{ display: 'none' }} onChange={handlePhotoUpload} />

            {(form.photos.length === 0 && form.docs.length === 0) ? (
              <label htmlFor="photoInput"
                style={{
                  display: 'block', border: '2px dashed var(--border2)', borderRadius: 10, padding: '32px 20px',
                  textAlign: 'center', cursor: 'pointer', transition: 'all .2s',
                  borderColor: errors.photos ? 'var(--danger)' : undefined
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = errors.photos ? 'var(--danger)' : 'var(--border2)'; e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text2)' }}>Click to upload photos from gallery</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>JPG, PNG, WEBP, PDF · Max 5MB per file · Up to 10 files</div>
              </label>
            ) : (
              <div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  {form.photos.map(p => (
                    <div key={p.id}
                      style={{
                        width: 90, height: 90, borderRadius: 8, overflow: 'hidden',
                        border: '1.5px solid var(--success-mid)', position: 'relative', cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.currentTarget.querySelector('.del').style.display = 'flex'}
                      onMouseLeave={e => e.currentTarget.querySelector('.del').style.display = 'none'}>
                      {p.preview ? (
                        <img src={p.preview} alt={p.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{
                          width: '100%', height: '100%', background: 'var(--success-bg)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28
                        }}>📷</div>
                      )}
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2px 4px',
                        background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 8.5, textAlign: 'center',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>{p.name}</div>
                      <div className="del" onClick={() => removePhoto(p.id)}
                        style={{
                          display: 'none', position: 'absolute', inset: 0, background: 'rgba(185,28,28,.8)',
                          borderRadius: 7, alignItems: 'center', justifyContent: 'center',
                          color: '#fff', fontSize: 20, fontWeight: 700
                        }}>✕</div>
                    </div>
                  ))}
                  {form.photos.length < 10 && (
                    <label htmlFor="photoInput"
                      style={{
                        width: 90, height: 90, borderRadius: 8, border: '2px dashed var(--border2)',
                        background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 24, color: 'var(--muted)', cursor: 'pointer', transition: 'all .15s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--muted)'; }}>
                      +
                    </label>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{form.photos.length} photo(s) uploaded</div>
              </div>
            )}
            {errors.photos && <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 8 }}>⚠ {errors.photos}</div>}

            {/* Document Upload */}
            <div style={{ marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                📄 Documents (Optional) <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>PDF, Word, Excel etc.</span>
              </div>
              <input type="file" id="docInput" accept=".pdf,.doc,.docx,.xls,.xlsx" multiple
                style={{ display: 'none' }} onChange={handleDocUpload} />
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {form.docs.map(d => (
                  <div key={d.id} style={{
                    padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8,
                    border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10,
                    position: 'relative'
                  }}>
                    <span style={{ fontSize: 18 }}>📄</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                      <div style={{ fontSize: 9, color: 'var(--muted)' }}>{d.size}</div>
                    </div>
                    <div onClick={() => removeDoc(d.id)} style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 800, fontSize: 14, padding: '0 4px' }}>✕</div>
                  </div>
                ))}
                <label htmlFor="docInput" style={{
                  padding: '10px 16px', borderRadius: 8, border: '2px dashed var(--border2)',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600,
                  color: 'var(--muted)', cursor: 'pointer', transition: 'all .2s'
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--muted)'; }}>
                  <span>+ Add Document</span>
                </label>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ── Bottom Buttons ── */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {step > 1 && (
            <Btn variant="outline" onClick={goBack}>← Back</Btn>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="outline" onClick={handleDraftSave}>💾 Save Draft</Btn>
          {step < 5 ? (
            <Btn variant="primary" onClick={goNext}>Next →</Btn>
          ) : (
            <Btn variant="primary" onClick={handleSubmit}>Submit for Approval ✓</Btn>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Modal open={showSuccess} onClose={() => onNav('dashboard')}
        title="Submission Successful" subtitle="Entry has been sent for admin approval"
        icon="✓" iconBg="var(--success-bg)"
        footer={<Btn variant="primary" onClick={() => onNav('dashboard')}>Go to Dashboard</Btn>}>
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 15 }}>📦</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 5 }}>Reference ID:</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', letterSpacing: '1.5px' }}>{refNum}</div>
        </div>
      </Modal>

      {/* Submission Loader Overlay */}
      {isSubmitting && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.8)', 
          zIndex: 9999, display: 'flex', flexDirection: 'column', 
          alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)'
        }}>
          <div className="spinner" style={{ 
            width: 50, height: 50, border: '5px solid var(--border)', 
            borderTopColor: 'var(--accent)', borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ marginTop: 15, fontWeight: 700, color: 'var(--text)' }}>Submitting Entry...</div>
          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}
    </div>
  );
}
